/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  compactAppliedFilters,
  type CustomFieldFilterMap,
  type LeadCountPreset,
  type TriState,
} from "./userManagementFilterUtils";

const filterFieldDefaults = {
  roleId: "",
  branchId: "",
  isActive: "all" as TriState,
  isTenantAdmin: "all" as TriState,
  startDate: "",
  endDate: "",
  leadCountType: "assigned" as "assigned" | "created" | "total",
  leadCountPreset: "all" as LeadCountPreset,
  minLeadCount: "",
  maxLeadCount: "",
};

export type UserListFilterFieldDefaults = typeof filterFieldDefaults;

const initialState = {
  search: "",
  // Blocked users are hidden from the roster by default and only appear
  // under the header's "Blocked Users" toggle — it is a VIEW, not one of the
  // drawer's filters, so it survives "Clear filters" and owns `isBlocked`
  // on the wire on its own (there is no Blocked field in the drawer).
  blockedView: false,
  ...filterFieldDefaults,
  cfFilters: {} as CustomFieldFilterMap,
  appliedFilters: {} as Record<string, any>,
};

export type UserListFilterState = typeof initialState;

const userListFilterSlice = createSlice({
  name: "userListFilter",
  initialState,
  reducers: {
    setUserListSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      if (action.payload) state.appliedFilters.search = action.payload;
      else delete state.appliedFilters.search;
    },
    setUserListFilterField(
      state,
      action: PayloadAction<{ key: keyof UserListFilterFieldDefaults; value: any }>
    ) {
      (state as any)[action.payload.key] = action.payload.value;
    },
    setUserListBlockedView(state, action: PayloadAction<boolean>) {
      state.blockedView = action.payload;
    },
    setUserListCustomField(
      state,
      action: PayloadAction<{ customInputId: string | number; value: string }>
    ) {
      const key = String(action.payload.customInputId);
      if (!action.payload.value || action.payload.value === "all") {
        delete state.cfFilters[key];
      } else {
        state.cfFilters[key] = action.payload.value;
      }
    },
    applyUserListFilters(state) {
      const applied: Record<string, any> = {
        ...(state.search ? { search: state.search } : {}),
        ...compactAppliedFilters(state as any, filterFieldDefaults, state.cfFilters),
      };
      if (
        !applied.leadCountPreset &&
        !applied.minLeadCount &&
        !applied.maxLeadCount
      ) {
        delete applied.leadCountType;
      }
      state.appliedFilters = applied;
    },
    clearUserListFilters(state) {
      // Clearing the drawer's filters must not silently drag the viewer out
      // of the Blocked Users view they are looking at.
      return { ...initialState, blockedView: state.blockedView };
    },
  },
});

export const {
  setUserListSearch,
  setUserListBlockedView,
  setUserListFilterField,
  setUserListCustomField,
  applyUserListFilters,
  clearUserListFilters,
} = userListFilterSlice.actions;

export default userListFilterSlice.reducer;
